'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'
import BrandFigure from '@/app/components/blog/BrandFigure'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Human Blood: What the 2022 Leslie Study Found"
      author="MicroPlastics Editorial Team"
      date="April 16, 2026"
      readTime="9 min read"
      heroImage="/blog/heroes/microplastics-in-human-blood.webp"
      heroImageAlt="Gloved laboratory researcher examining a capped blood sample beside a microscope"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        In March 2022, scientists at Vrije Universiteit Amsterdam published the first
        confirmed detection of microplastics in human blood. They found plastic
        particles in <strong>17 of 22 healthy adult donors (77%)</strong>, most commonly
        PET (used in drink bottles), polystyrene (food packaging), and polyethylene.
        Average concentration was <strong>1.6 µg/mL of blood</strong>. Subsequent studies
        through 2025 have confirmed the finding and detected nanoplastics in arteries,
        lungs, placenta, and brain tissue.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>The Leslie et al. 2022 study in <em>Environment International</em> was the first peer-reviewed evidence that ingested or inhaled microplastics reach the human bloodstream.</>,
          <>PET (polyethylene terephthalate) was the most common plastic detected, the same material as single-use water bottles.</>,
          <>Particles down to 700 nanometres were detectable; smaller nanoplastics likely exist but were below the study&apos;s detection limit.</>,
          <>Follow-up studies have since found microplastics in placenta, lung tissue, semen, breast milk, atherosclerotic plaque, and brain tissue.</>,
          <>No safe blood concentration has been established. The 2024 NEJM cardiovascular study suggests presence in tissue is associated with adverse outcomes.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The 2022 Leslie study, in plain English
      </h2>
      <p>
        Heather Leslie and Marja Lamoree of Vrije Universiteit Amsterdam analysed
        whole-blood samples from 22 healthy anonymous donors. Using pyrolysis gas
        chromatography–mass spectrometry, a technique that vaporises tiny samples
        and identifies them by their chemical fingerprint, they detected
        microplastic particles in <strong>17 of the 22 donors</strong> (~77%). The
        average quantifiable concentration was <strong>1.6 micrograms per millilitre</strong>
        of blood.
      </p>
      <p>
        Five polymers were screened. The breakdown of what they found is striking:
      </p>

      <BlogComparisonTable
        caption="Microplastic polymers detected in human blood (Leslie et al. 2022)"
        columns={[
          { key: 'polymer', label: 'Polymer' },
          { key: 'common', label: 'Common products' },
          { key: 'detection', label: 'Detected in donors' },
        ]}
        rows={[
          { polymer: 'PET (polyethylene terephthalate)', common: 'Single-use bottles, polyester clothing', detection: '50% of samples' },
          { polymer: 'Polystyrene', common: 'Food packaging, take-out containers', detection: '36%' },
          { polymer: 'Polyethylene', common: 'Plastic bags, films, milk jugs', detection: '23%' },
          { polymer: 'Poly(methyl methacrylate) (PMMA)', common: 'Acrylic plastics, dental fillings', detection: 'Below quantification limit' },
          { polymer: 'Polypropylene', common: 'Yogurt cups, bottle caps', detection: 'Below quantification limit' },
        ]}
      />

      <BrandFigure

        src="/social/stat-blood.webp"

        alt="Stat card: microplastics have been found in human blood, detected in 77% of people tested in the first study to look. Source: Environment International, 2022."

        width={900}

        height={900}

      />


      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How does plastic get into blood?
      </h2>
      <p>
        Three routes are established in the toxicology literature:
      </p>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Ingestion.</strong> Microplastics in food and beverages can pass
          through the gut wall via M-cells and Peyer&apos;s patches, especially particles
          smaller than 10 µm. Nanoplastics &lt;1 µm cross more readily.
        </li>
        <li>
          <strong>Inhalation.</strong> Airborne microplastics, indoor air contains
          3–15× more than outdoor air, settle in airways. Fibres &lt;5 µm reach
          alveoli and can enter pulmonary circulation. See our{' '}
          <Link href="/blog/airborne-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
            airborne microplastics guide
          </Link>.
        </li>
        <li>
          <strong>Medical sources.</strong> The Leslie study explicitly notes that IV
          drips, blood-bag plasticisers, and dental polymers may contribute to baseline
          contamination, an area still being characterised.
        </li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What follow-up research has found since 2022
      </h2>
      <p>
        The Leslie study triggered a wave of replication and extension. As of mid-2026
        the catalogue of human tissues confirmed to contain microplastics or
        nanoplastics includes:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Placenta</strong> (Ragusa et al., 2021)</li>
        <li><strong>Lung tissue</strong> (Jenner et al., 2022)</li>
        <li><strong>Breast milk</strong> (Ragusa et al., 2022), see our{' '}
          <Link href="/blog/microplastics-in-breast-milk" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
            breast-milk article
          </Link>
        </li>
        <li><strong>Testes and semen</strong> (Hu et al., 2024; Zhao et al., 2024)</li>
        <li><strong>Atherosclerotic plaque</strong> (Marfella et al., NEJM 2024), see our{' '}
          <Link href="/blog/microplastics-and-cancer" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
            cardiovascular evidence article
          </Link>
        </li>
        <li><strong>Brain tissue</strong> (Nihart et al., 2025), with concentrations
          ~7× higher in patients with documented dementia. See our{' '}
          <Link href="/blog/microplastics-in-the-brain" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
            brain microplastics article
          </Link>
        </li>
        <li><strong>Liver, kidney, spleen</strong> (multiple papers, 2023–2025)</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Is plastic in your blood dangerous?
      </h2>
      <p>
        Honestly: we are still in the &ldquo;establishing association&rdquo; phase. No
        regulatory body has yet set a maximum safe blood concentration. What we do
        know:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Inflammation:</strong> in-vitro and animal studies consistently
          show micro- and nanoplastic particles trigger oxidative stress and
          inflammatory cytokines in vascular cells.
        </li>
        <li>
          <strong>Endocrine disruption:</strong> plastics carry plasticisers (phthalates,
          bisphenols) and additives that interfere with hormone signalling at
          extremely low doses.
        </li>
        <li>
          <strong>Cardiovascular events:</strong> the 2024 NEJM study reported a
          <strong> 4.53× higher risk</strong> of heart attack, stroke, or death from
          any cause over 34 months in patients whose carotid plaque contained
          microplastics versus those whose plaque did not.
        </li>
      </ul>
      <p>
        These findings are correlational and the field has not yet established
        causation, but the trend is consistent and the patient evidence is
        accumulating quickly.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Can you reduce microplastics already in your bloodstream?
      </h2>
      <p>
        Direct &ldquo;detox&rdquo; is largely marketing, see our{' '}
        <Link href="/blog/7-day-microplastic-detox-challenge" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          evidence-based detox guide
        </Link>{' '}
        for what is and isn&apos;t supported. The body does excrete some particles via
        bile, urine, and faeces, but accumulation in fat and connective tissue
        appears to be lifelong. Realistic strategy: <strong>reduce intake going forward</strong>.
      </p>
      <p>
        The five daily changes that do the most (consistent across every published
        exposure-reduction study):
      </p>
      <ol className="list-decimal pl-6 space-y-2">
        <li>Switch bottled water to filtered tap water (RO or carbon block)</li>
        <li>Never heat food or water in plastic</li>
        <li>Replace plastic food storage with glass or stainless steel</li>
        <li>Choose natural-fibre clothing and bedding</li>
        <li>HEPA-filter indoor air, especially in bedrooms</li>
      </ol>


      <BlogWhatTheAppChecks
        items={[
          <>Product packaging. PET, HDPE, PP, PS, PVC, multi-layer, glass, aluminum.</>,
          <>Container condition from photo, scratches, dents, fade.</>,
          <>Brand and product category, flags for known PFAS / BPA / fragranced lines.</>,
          <>Use-context flags, heat exposure, microwave, reuse cycles.</>,
          <>Cited published research, every score links the specific studies behind it.</>,
        ]}
      />

      <BlogAppCTA
        headline="Translate the research into 5-second shelf decisions"
        sub="Reading the studies is step one. Acting on them at the grocery store is step two. The MicroPlastics app scores each product 0–100 using research like this."
        buttonLabel="Get the MicroPlastics app"
      />

      <BlogFAQ
        items={[
          {
            question: 'When was the first study of microplastics in human blood?',
            answer:
              'March 2022. Heather Leslie and colleagues at Vrije Universiteit Amsterdam published the first peer-reviewed detection in Environment International, finding microplastics in 17 of 22 healthy adult blood donors (77%).',
          },
          {
            question: 'How much plastic is in human blood?',
            answer:
              'The 2022 study measured an average concentration of 1.6 micrograms of plastic per millilitre of blood across donors with detectable particles. Concentrations vary widely between individuals.',
          },
          {
            question: 'What types of plastic were found in human blood?',
            answer:
              'PET (single-use bottles, polyester clothing) in 50% of samples, polystyrene (food packaging) in 36%, and polyethylene (bags and films) in 23%. PMMA and polypropylene were detected in some donors but below quantification limits.',
          },
          {
            question: 'How does plastic get into your bloodstream?',
            answer:
              'Three main routes: ingestion through food and water (small particles cross the gut wall), inhalation of airborne particles (smallest fibres reach pulmonary circulation), and medical exposure such as IV equipment and blood bags.',
          },
          {
            question: 'Is having microplastics in your blood dangerous?',
            answer:
              'No safe blood concentration has been established. A 2024 NEJM study found patients whose arterial plaque contained microplastics had a 4.53× higher risk of cardiovascular events over 34 months, the first strong human evidence of harm.',
          },
          {
            question: 'Can you remove microplastics already in your blood?',
            answer:
              'There is no proven medical detox for blood-borne microplastics. The body excretes some particles naturally but accumulation in tissue appears to be lifelong. Reducing future exposure is the only intervention with evidence support.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Leslie HA, van Velzen MJM, Brandsma SH, et al.',
            year: 2022,
            title: 'Discovery and quantification of plastic particle pollution in human blood',
            publisher: 'Environment International',
            url: 'https://www.sciencedirect.com/science/article/pii/S0160412022001258',
          },
          {
            authors: 'Ragusa A, Svelato A, Santacroce C, et al.',
            year: 2021,
            title: 'Plasticenta: First evidence of microplastics in human placenta',
            publisher: 'Environment International',
            url: 'https://www.sciencedirect.com/science/article/pii/S0160412020322297',
          },
          {
            authors: 'Jenner LC, Rotchell JM, Bennett RT, et al.',
            year: 2022,
            title: 'Detection of microplastics in human lung tissue using μFTIR spectroscopy',
            publisher: 'Science of the Total Environment',
            url: 'https://www.sciencedirect.com/science/article/pii/S0048969722023178',
          },
          {
            authors: 'Marfella R, Prattichizzo F, Sardu C, et al.',
            year: 2024,
            title: 'Microplastics and nanoplastics in atheromas and cardiovascular events',
            publisher: 'New England Journal of Medicine',
            url: 'https://www.nejm.org/doi/full/10.1056/NEJMoa2309822',
          },
          {
            authors: 'Nihart AJ, Garcia MA, El Hayek E, et al.',
            year: 2025,
            title: 'Bioaccumulation of microplastics in decedent human brains',
            publisher: 'Nature Medicine',
            url: 'https://www.nature.com/articles/s41591-024-03453-1',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
